import { useStripe } from '@stripe/react-stripe-js';
import type { PaymentRequest } from '@stripe/stripe-js';
import { useEffect, useMemo, useRef, useState } from 'react';

export type UseApplePayRequestParams = {
  amountCents: Cent;
  label: string;
};

export type UseApplePayRequestReturn = {
  canMakePayment: boolean;
  paymentRequest: PaymentRequest | null;
};

/**
 * Creates a Stripe PaymentRequest for Apple Pay / Google Pay.
 *
 * Automatically renders as Apple Pay on Safari and Google Pay on Chrome.
 * `canMakePayment` is true only when the browser/device supports it and
 * the user has a saved payment method available.
 *
 * Must be called inside a Stripe <Elements> provider.
 */
export const useApplePayRequest = ({ amountCents, label }: UseApplePayRequestParams): UseApplePayRequestReturn => {
  const stripe = useStripe();
  const [canMakePayment, setCanMakePayment] = useState(false);
  const isReadyRef = useRef(false);

  // Only create a new PaymentRequest when the stripe instance changes.
  // Amount/label updates are handled via paymentRequest.update() below.
  const paymentRequest = useMemo(() => {
    if (!stripe) return null;
    // eslint-disable-next-line fp/no-mutation -- reset readiness when stripe instance changes
    isReadyRef.current = false;
    return stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      requestPayerEmail: true,
      requestPayerName: true,
      total: { amount: amountCents, label },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally omit amount/label; updates handled separately
  }, [stripe]);

  useEffect(() => {
    if (!paymentRequest) return;
    paymentRequest
      .canMakePayment()
      .then((result) => {
        // eslint-disable-next-line fp/no-mutation -- mark ready so update() calls are safe
        isReadyRef.current = true;
        setCanMakePayment(Boolean(result));
      })
      .catch(() => {
        setCanMakePayment(false);
      });
  }, [paymentRequest]);

  // Keep the displayed amount/label in sync without recreating the PaymentRequest.
  // update() must only be called after canMakePayment() has resolved.
  useEffect(() => {
    if (!paymentRequest || !isReadyRef.current) return;
    paymentRequest.update({ total: { amount: amountCents, label } });
  }, [paymentRequest, amountCents, label]);

  return { canMakePayment, paymentRequest };
};
